import {
  useState, useRef, useEffect, useContext,
} from 'react';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  useLiveAgentSession, useDrawerPrefs, createActionId, getDefaultSession,
  shouldResetSession, reconcileSession, sessionRecord, drawerPrefsRecord,
} from '../platform/session/session';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { emit } from '../platform/events/lifecycle';
import { emitHeaderEntryClick } from '../shared/analytics';
import { HEADER_ENTRY } from '../shared/assistant-config';

/**
 * The Shell's EAGER half: the drawer open/close/minimize state machine + the FAB's
 * behavior. It lives in the main chunk (not the lazy Shell view) so the FAB and drawer
 * state work before the assistant content loads. App (bootstrap) composes this hook and
 * renders the FAB + lazy Shell from its outputs.
 *
 * It owns `drawerState` (ephemeral) AND the *persisted* drawer-behavior flags
 * (`shouldAutoOpen`, `hasSessionInteractedWithFab`) via the `maa-drawer-prefs` record
 * (Seq B.3c), plus cross-tab drawer-state sync and the auto-open/orientation transitions.
 * It also owns the `maa-session` identity record's lifecycle — seeding it, reconciling it
 * against peer tabs, and renewing it on the reset rules — and cross-READS escalation from
 * `maa-live-agent`, which it never writes.
 *
 * Nothing here mirrors a record in a ref any more. `sessionRecord.get()` / `drawerPrefsRecord
 * .get()` read storage, so they are already the synchronous authority the fab-click actionId
 * and the mount-time reset check need, and `update()` merges onto that current value, so this
 * hook and the FAB cannot revert each other (the WS-3 lost-update hazard is now structural
 * rather than something each writer remembers to guard).
 *
 * Residual coupling (noted for WS-11): drawer control is inseparable from the persisted
 * session because auto-open + fab-interaction must survive reloads/tabs. Modeling those
 * flags as first-class drawer state (rather than session fields) is future Shell work.
 *
 * This hook takes no options: it handles only drawer traffic on the shared assistant
 * BroadcastChannel and relays nothing. (It used to accept an `onBroadcastMessage` escape
 * hatch so App could receive product-pod analytics that pods posted onto the same channel —
 * a same-tab child→ancestor hop routed through the cross-tab bus. Seq D.1 made the pods emit
 * their analytics directly, so the relay is gone.)
 */
export const useDrawerController = () => {
  const { isConsumerApp, customer } = useContext(ExperienceContext);
  // Escalation is a cross-READ from the live-agent record (its sole writer is the liveAgent
  // block). It lived on the shared session record before B.3b; reading it from maa-session
  // here would now be stale.
  const [liveAgent] = useLiveAgentSession();
  // Persisted drawer prefs (shouldAutoOpen, hasSessionInteractedWithFab). This hook and the
  // FAB are the writers, and both write through `update`, which merges onto the record's
  // current value — so neither can revert the other and neither needs a ref.
  const [drawerPrefs, updatePrefs] = useDrawerPrefs();
  const { lesser: isMobile } = useBreakpoint('lg');
  // Do not use this setter directly, use updateDrawerState instead.
  const [drawerState, setDrawerState] = useState('CLOSED');
  // Lazy-init so the channel is created once, not re-allocated (and leaked) on every render.
  const broadcastChannel = useRef(null);
  if (!broadcastChannel.current) {
    broadcastChannel.current = createBroadcastChannel(CHANNELS.assistant);
  }
  // Mini drawer is disabled for B2B customers and while escalated to an agent (design decision).
  const enableMiniDrawer = customer?.type !== 'b2b' && !liveAgent?.isEscalated;

  const updateDrawerState = (newDrawerState, { skipBroadcast } = {}) => {
    setDrawerState(newDrawerState);
    if (!skipBroadcast) {
      // App opens new tabs instead of reloading; keep drawer state in sync across app tabs.
      broadcastChannel.current.postMessage({
        type: 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED',
        data: { drawerState: newDrawerState }
      });
    }
  };

  const handleOpen = (isUserInteraction = false, origin = HEADER_ENTRY.ORIGIN_FAB) => {
    const isFromHeader = origin === HEADER_ENTRY.ORIGIN_HEADER;

    if (isUserInteraction && isFromHeader) emitHeaderEntryClick(HEADER_ENTRY.OPEN_LABEL);

    updateDrawerState('OPEN');
    // Read the prior flag before the update, so the fab-click event still fires on the first
    // interaction of the session.
    const alreadyInteracted = drawerPrefsRecord.get()?.hasSessionInteractedWithFab;
    updatePrefs({
      hasSessionInteractedWithFab: alreadyInteracted || isUserInteraction
    });
    // The cadences differ: analytics counts every header ask, fab-click stays once per
    // session. Do not fold them back together.
    if (isUserInteraction && !isFromHeader && !alreadyInteracted) {
      const { sessionId } = sessionRecord.get() ?? {};
      emit('magic-apron-assistant.fab-click', {
        actionID: createActionId(sessionId),
        sessionID: sessionId,
      });
    }
  };

  // The drawer-control command handed down to the assistant subtree (DrawerControlContext).
  // Opens THIS tab directly — no self-addressed broadcast — and tells peer tabs to open too,
  // which is deliberate behavior (user-confirmed 2026-07-27) rather than the accident it used
  // to be. Posting from this hook's OWN channel object is what makes the two paths compose:
  // the HTML spec's broadcast algorithm removes the sending object from the destination list
  // (https://html.spec.whatwg.org/multipage/web-messaging.html#broadcasting-to-other-browsing-contexts),
  // so this tab cannot receive its own post and double-open, while every other tab's
  // controller still does. That guarantee is why the cross-tab hop had to move here, to the
  // owner of drawer state, instead of staying with whichever component wanted the drawer.
  const openDrawer = () => {
    handleOpen(false);
    broadcastChannel.current.postMessage({ type: 'MAA_OPEN_DRAWER' });
  };

  // Closing does NOT announce `isReady: false`, and must not. Readiness is the assistant's
  // ability to accept a question, which closing the drawer does not revoke — a closed drawer
  // reopens on MAA_OPEN_DRAWER and answers just the same. The retraction that used to live
  // here latched the host page's entry points on "not ready" for the rest of the page's life
  // (nothing re-announced: useMessageHistory's init() is one-shot), so every entry-point
  // suggested question after the first close timed out and was dropped. See the readiness
  // contract in platform/events/lifecycle.js.
  //
  // `origin` is compared strictly because this is also passed straight to `onClose`
  // handlers, which call it with a click event.
  const handleClose = (origin = HEADER_ENTRY.ORIGIN_FAB) => {
    if (origin === HEADER_ENTRY.ORIGIN_HEADER) emitHeaderEntryClick(HEADER_ENTRY.CLOSE_LABEL);

    updateDrawerState('CLOSED');
    updatePrefs({ shouldAutoOpen: false });
  };

  // Translate a target drawer state coming from the draggable-drawer UI into the
  // open/close/minimize handlers. A drag-to-open is a user interaction, so OPEN routes
  // through handleOpen(true).
  const handleDrawerStateChange = (newDrawerState) => {
    if (newDrawerState === 'CLOSED') handleClose();
    if (newDrawerState === 'OPEN') handleOpen(true);
    if (newDrawerState === 'MINIMIZED') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
      updatePrefs({ shouldAutoOpen: true });
    }
  };

  // Host asks a question on the user's behalf → remember to auto-open next time.
  const markShouldAutoOpen = () => updatePrefs({ shouldAutoOpen: true });

  useEffect(() => {
    // Persist the identity if this is the first document to look. Every other holder — the
    // orchestrator and the live-agent bridge, both in lazy chunks — then reads that value back
    // instead of resolving one of its own.
    sessionRecord.seed();

    // Concurrent tabs can each find the record absent on their first render and each mint a
    // fallback id; only one write survives and nothing invalidates the loser's mirror. Adopt
    // whatever this tab and its peers all resolve to, before anything has read the id.
    // Mount-only, for the same reason the reset below is.
    const reconciled = reconcileSession(sessionRecord.get());
    if (reconciled?.sessionId && reconciled.sessionId !== sessionRecord.get()?.sessionId) {
      sessionRecord.set(reconciled);
    }

    // Identity-owned reset rules (see shouldResetSession): a fallback id ages out on the 24h
    // timer, a thda.s id is replaced when the cookie rotates, and a 7 day backstop applies to
    // either. Evaluated on mount ONLY, so nobody is reset mid-conversation. Read the outgoing
    // prefs before the reset replaces them.
    const willReset = shouldResetSession(sessionRecord.get());
    const outgoingPrefs = drawerPrefsRecord.get();
    const shouldAutoOpen = willReset
      ? outgoingPrefs.shouldAutoOpen !== false
      : outgoingPrefs?.shouldAutoOpen;

    if (willReset) {
      // Renew identity and reset drawer prefs (carrying shouldAutoOpen). The live-agent record
      // is intentionally NOT reset here — single-writer: the liveAgent block owns it (its
      // idle-TTL clears a stale escalation when the block starts). A reset renews identity +
      // drawer prefs only. `getDefaultSession()` runs now, so sessionId/timestamp re-resolve
      // at reset time rather than whenever this module was evaluated.
      sessionRecord.set(getDefaultSession());
      drawerPrefsRecord.set({
        shouldAutoOpen,
        hasSessionInteractedWithFab: false,
        pagesExpandedFABShownOn: [],
      });
    }

    if (shouldAutoOpen) {
      let autoOpenState = 'OPEN';
      if (isMobile) autoOpenState = enableMiniDrawer ? 'MINIMIZED' : 'CLOSED';
      updateDrawerState(autoOpenState);
    }

    // Cross-tab drawer traffic. This hook owns every message it handles here — it no
    // longer relays anything it doesn't own (Seq D.1 removed the product-pod analytics
    // forward, which was a same-tab child→ancestor hop routed through the cross-tab bus).
    broadcastChannel.current.onmessage = ({ data: { type, data } }) => {
      if (type === 'MAA_OPEN_DRAWER') {
        handleOpen(false);
      } else if (type === 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED' && isConsumerApp) {
        // Cross-tab drawer state synchronization for the mobile app.
        updateDrawerState(data.drawerState, { skipBroadcast: true });
      }
    };

    return () => {
      broadcastChannel.current.close();
    };
  }, []);

  useEffect(() => {
    emit('magic-apron-assistant.drawer-minimize', drawerState);
  }, [drawerState]);

  // On tablets, changing orientation can flip the layout between open and minimized.
  useEffect(() => {
    if (isMobile && drawerState === 'OPEN') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
    }
    if (!isMobile && drawerState === 'MINIMIZED') {
      updateDrawerState('OPEN');
    }
  }, [isMobile]);

  return {
    drawerState,
    handleOpen,
    handleClose,
    handleDrawerStateChange,
    markShouldAutoOpen,
    openDrawer,
  };
};
